/** @format */

import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { filter, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { Store } from '@ngrx/store';
import {
	Chain,
	Environment,
	HeaderStoreSelection,
	ISkuLocation,
	ProductSku,
	SKUResolveEvent
} from '@shared/data-access/interfaces';
import { ProductAnalyticsService } from '@shared/data-access/analytics';
import { StoreService, UtilityService } from '@shared/data-access/services';
import { setBopisInventory, setEcommInventory, setResolvedSku } from './product-selection.actions';
import {
	selectBopisInventory,
	selectEcommInventory,
	selectIsResolvedSkuStorePickupAvailable,
	selectNearbyAvailableStoreCount,
	selectResolvedSku,
	selectSkusWithInventory
} from './product-selection.selectors';
import { selectUserStoreInfo, setNearbyStores } from '@state/data-access';
// Needs to be like this to prevent circular dependency
import { selectAtcUserInputQty } from '../../../../add-to-cart/data-access/src/lib/+state/add-to-cart.selectors';
import { concatLatestFrom } from '@ngrx/operators';

@Injectable()
export class ProductSelectionEffects {
	private readonly actions$ = inject(Actions);
	private readonly store = inject(Store);
	private readonly productAnalyticsService = inject(ProductAnalyticsService);
	private readonly utilityService = inject(UtilityService);
	private readonly storeService = inject(StoreService);
	private readonly environment = inject(Environment);
	private readonly chain = inject(Chain);

	private readonly stopWaitingAnalyticsEvents$ = new Subject<boolean>();

	fireResolveSkuAnalyticsEvent$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(setResolvedSku),
				filter(({ resolvedSku }) => !!resolvedSku),
				concatLatestFrom(() => [
					this.store.select(selectAtcUserInputQty),
					this.store.select(selectEcommInventory),
					this.store.select(selectBopisInventory),
					this.store.select(selectNearbyAvailableStoreCount),
					this.store.select(selectIsResolvedSkuStorePickupAvailable)
				]),
				// wait for store info to exist before firing
				switchMap((data) => {
					// prevent subscription from firing twice if resolved sku changes while waiting for store data
					this.stopWaitingAnalyticsEvents$.next(true);

					return this.store.select(selectUserStoreInfo).pipe(
						takeUntil(this.stopWaitingAnalyticsEvents$),
						filter((storeInfo) => storeInfo !== null),
						take(1),
						map((userSelectedStoreData) => ({ data, userSelectedStoreData }))
					);
				}),
				tap(
					({
						data: [
							{ resolvedSku },
							userInputQuantity,
							ecommInventory,
							bopisInventory,
							nearbyStoreCount,
							isSkuAvailableAtSelectedStore
						],
						userSelectedStoreData
					}) => {
						if (resolvedSku && userSelectedStoreData) {
							this.productAnalyticsService.triggerAnalyticsEvent(
								'SKUResolve',
								this.buildSkuResolveAnalyticsData(
									resolvedSku,
									userSelectedStoreData,
									userInputQuantity,
									ecommInventory,
									bopisInventory,
									nearbyStoreCount,
									isSkuAvailableAtSelectedStore
								)
							);
						}
					}
				)
			),
		{ dispatch: false }
	);

	buildSkuResolveAnalyticsData = (
		resolvedSku: ProductSku,
		userSelectedStoreData: HeaderStoreSelection,
		userInputQuantity: number,
		ecommInventory: ISkuLocation[],
		bopisInventory: ISkuLocation[],
		nearbyStoreCount: number,
		isSkuAvailableAtSelectedStore: boolean
	): SKUResolveEvent => {
		const availableToShipAtsQtyInventory = parseInt(
			this.storeService.getSkuInventoryFromLocations(ecommInventory, resolvedSku.partNumber)
				?.atsqty,
			10
		);

		const skuBopisInventory = this.storeService.getSkuInventoryFromLocations(
			bopisInventory,
			resolvedSku.partNumber
		);

		const bopisAtsQtyInventory = parseInt(skuBopisInventory?.atsqty, 10);
		const bopisIsaQtyInventory = parseInt(skuBopisInventory?.isaqty, 10);

		const isProductAvailableForPickup = this.isProductAvailableForPickup(
			resolvedSku,
			userInputQuantity,
			bopisAtsQtyInventory
		);
		const isProductAvailableInStore = this.isProductAvailableInStore(
			resolvedSku,
			userInputQuantity,
			bopisIsaQtyInventory
		);

		const isSkuBuyable = this.utilityService.isTruthy(resolvedSku?.buyable);

		const doesSkuHaveEcommInventory = availableToShipAtsQtyInventory > 0;

		const notifyUserWhenAvailableKillSwitchEnabled =
			this.environment.killSwitch.notifyUserWhenAvailableEnabled[
				this.chain.chainIdentifierAbbr
			];

		// SET OBJECT FOR ANALYTICS EVENT DATA
		return {
			ecode: resolvedSku.parentPartNumber,
			sku: resolvedSku,
			AvailableForPickup: isProductAvailableForPickup,
			AvailableInStore: isProductAvailableInStore,
			AvailableToShip: availableToShipAtsQtyInventory > 0,
			QuantityRequested: userInputQuantity,
			lowStockShipMessageShown:
				!isNaN(availableToShipAtsQtyInventory) &&
				availableToShipAtsQtyInventory < 10 &&
				availableToShipAtsQtyInventory > 0 &&
				isSkuBuyable,
			lowStockShipQuantityAvailable: availableToShipAtsQtyInventory || 0,
			lowStockBopisMessageShown:
				!isNaN(bopisAtsQtyInventory) &&
				isProductAvailableInStore &&
				bopisAtsQtyInventory < 10 &&
				bopisAtsQtyInventory > 0 &&
				isSkuBuyable,
			lowStockBopisQuantityAvailable: bopisAtsQtyInventory || 0,
			lowStockNearbyStoresMessageShown:
				nearbyStoreCount > 0 && !isSkuAvailableAtSelectedStore,
			lowStockNearbyStoresCount: nearbyStoreCount,
			lowStockNearbyStoresCurrentStoreNumber:
				userSelectedStoreData.store || userSelectedStoreData.location,
			CurrentStoreNumber: this.utilityService.convertToNumber(
				userSelectedStoreData.store || userSelectedStoreData.location
			),
			notifyMeShown:
				notifyUserWhenAvailableKillSwitchEnabled &&
				isSkuBuyable &&
				(!isProductAvailableInStore || !doesSkuHaveEcommInventory)
		};
	};

	/* Check for products' availability for in-store based on IsaQty */
	isProductAvailableInStore(
		resolvedSkuData: ProductSku,
		userInputQuantity: number,
		bopisIsaQtyInventory: number
	): boolean {
		return resolvedSkuData?.isaFlag && bopisIsaQtyInventory >= userInputQuantity;
	}

	/* Check for products' availability for in-store pickup based on atsQty and bopis */
	isProductAvailableForPickup(
		resolvedSkuData: ProductSku,
		userInputQuantity: number,
		bopisAtsQtyInventory: number
	): boolean {
		return resolvedSkuData?.bopisFlag && bopisAtsQtyInventory >= userInputQuantity;
	}

	checkIfResolvedSkuHasInventory$ = createEffect(() => {
		const noOp = { type: '[Product Selection] Do Nothing' };
		return this.actions$.pipe(
			ofType(setNearbyStores, setEcommInventory, setBopisInventory),
			concatLatestFrom(() => [
				this.store.select(selectSkusWithInventory),
				this.store.select(selectResolvedSku)
			]),
			map(([, skusWithInventory, resolvedSku]) => {
				if (!resolvedSku) {
					return noOp;
				}
				const resolvedSkuHasInventory = skusWithInventory.skus.some(
					(sku) => sku.partNumber === resolvedSku.partNumber
				);
				if (resolvedSkuHasInventory) {
					return noOp;
				}
				// Use this action for now since we currently have multiple effects all listening to this
				// Eventually we'll want to have one SKU resolution effect that handles all of this logic so we only fire 1 resolve SKU action
				return setResolvedSku({ resolvedSku: undefined });
			}),
			filter((action) => action.type !== noOp.type)
		);
	});
}
